import React, { useState } from "react";
import { Section, Eyebrow, SectionTitle, currency } from "@/components/Primitives";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, ShieldCheck } from "lucide-react";

const STEPS = ["Requested", "Contractor Assigned", "Materials Delivered", "In Progress", "Inspection", "Payment Released"];

const ROLE_INFO = {
  homeowner: ["Post your job for free with photos and budget.", "Get connected to verified contractors nearby.", "Track progress from your dashboard, milestone by milestone.", "Release payment only when the work is done right."],
  contractor: ["Submit WSIB & insurance to unlock customer job listings.", "Browse and connect to jobs that match your trade.", "Pay a small connection fee — win the contract.", "Update progress and get paid on milestones."],
  laborer: ["Create a basic profile and list your skills.", "Find paid gigs on active job sites near you.", "Connect directly with contractors who need hands.", "Build ratings that unlock better-paying work."],
  realtor: ["List properties for sale, rent or investment flips.", "Surface cap rate, ARV and rehab budgets to investors.", "Connect with capital and buyers via priority fee.", "Loop in verified contractors for rehab estimates."],
  driver: ["Register your cargo van, Sprinter or SUV.", "Claim delivery runs from Home Depot & Lowe's.", "Haul materials straight to the job site.", "Get paid per run, keep 100% of your payout."],
};

const HowItWorks = () => {
  const [budget, setBudget] = useState(25000);
  const feeRate = 0.05;

  return (
    <div>
      <div className="eti-grid-bg border-b border-white/10 py-16">
        <Section>
          <Eyebrow>Services & How It Works</Eyebrow>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-white leading-[1.05]">From Connection<br />To Completion</h1>
        </Section>
      </div>

      <Section className="py-14">
        <Eyebrow>Role By Role</Eyebrow>
        <SectionTitle>Pick Your Path</SectionTitle>
        <Tabs defaultValue="homeowner" className="mt-8">
          <TabsList className="bg-secondary flex-wrap h-auto">
            {Object.keys(ROLE_INFO).map((r) => (
              <TabsTrigger key={r} value={r} className="capitalize data-[state=active]:bg-primary data-[state=active]:text-white" data-testid={`hiw-tab-${r}`}>{r}</TabsTrigger>
            ))}
          </TabsList>
          {Object.entries(ROLE_INFO).map(([r, steps]) => (
            <TabsContent key={r} value={r} className="mt-6">
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {steps.map((s, i) => (
                  <div key={i} className="eti-card rounded-lg p-5" data-testid={`hiw-${r}-step-${i}`}>
                    <span className="font-mono text-2xl font-bold text-white/15">0{i + 1}</span>
                    <p className="mt-2 text-sm text-slate-300 leading-relaxed">{s}</p>
                  </div>
                ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </Section>

      <Section className="py-8">
        <div className="eti-card rounded-2xl p-8">
          <Eyebrow>Verification Requirement</Eyebrow>
          <SectionTitle>WSIB & Insurance Gate</SectionTitle>
          <div className="mt-6 grid sm:grid-cols-3 gap-4">
            {["Upload WSIB Clearance Certificate", "Upload Commercial Liability Insurance", "Instant verified badge unlocks job listings"].map((t, i) => (
              <div key={i} className="flex gap-3 items-start">
                <ShieldCheck className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <p className="text-sm text-slate-300">{t}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      <Section className="py-14">
        <Eyebrow>The Milestone Flow</Eyebrow>
        <SectionTitle>Track Every Step</SectionTitle>
        <div className="mt-8 flex flex-col sm:flex-row gap-2">
          {STEPS.map((s, i) => (
            <div key={i} className="flex-1 eti-card rounded-lg p-4 flex sm:flex-col items-center gap-2 text-center" data-testid={`hiw-flow-${i}`}>
              <div className="h-8 w-8 rounded-full bg-primary/15 flex items-center justify-center font-mono text-sm text-primary shrink-0">{i + 1}</div>
              <p className="text-xs text-slate-300 font-medium">{s}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section className="py-8 pb-16">
        <div className="eti-card rounded-2xl p-8 max-w-2xl">
          <Eyebrow>ROI Calculator</Eyebrow>
          <SectionTitle>Estimate Your Connection Fee</SectionTitle>
          <p className="mt-3 text-sm text-slate-400">Exclusive Trades takes a small, transparent fee on connections. Slide to your project budget.</p>
          <div className="mt-6">
            <Label className="text-slate-300 text-sm">Project Budget: <span className="text-primary font-mono">{currency(budget)}</span></Label>
            <input type="range" min="1000" max="200000" step="1000" value={budget} onChange={(e) => setBudget(Number(e.target.value))}
              className="w-full mt-3 accent-[hsl(var(--primary))]" data-testid="hiw-roi-slider" />
          </div>
          <div className="mt-6 grid grid-cols-2 gap-4">
            <div className="bg-secondary rounded-lg p-4"><p className="text-xs text-slate-400">Est. Connection Fee (5%)</p><p className="font-display text-2xl font-bold text-primary">{currency(budget * feeRate)}</p></div>
            <div className="bg-secondary rounded-lg p-4"><p className="text-xs text-slate-400">You Keep</p><p className="font-display text-2xl font-bold text-white">{currency(budget * (1 - feeRate))}</p></div>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default HowItWorks;
